import React from 'react';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
} from '@order/components';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import {
  useCustomerProfileFieldsQuery,
  useUpdateCustomerProfileMutation,
} from './GraphQL/Profile.generated';
import { ProfileForm } from './ProfileForm';

// ────────────────────────────────────────────────────────────────────────────────

export const ProfileScreen = (): React.ReactElement => {
  const { t } = useLocalizationContext();

  const [customerResponse] = useCustomerProfileFieldsQuery();
  const { submitResponse, handleFormSubmit } = useFormSubmit();

  const isLoading =
    customerResponse.fetching || !customerResponse.data?.currentCustomer?.id;

  return (
    <AccountScreenContainer screen="profile" testID="profile-screen">
      <AccountScreenTitle title={t('account.profile.title')} />
      <AccountScreenSection>
        <ProfileForm
          customer={customerResponse.data?.currentCustomer ?? null}
          isLoading={isLoading}
          isSubmitting={submitResponse.fetching}
          onSaveCustomer={handleFormSubmit}
        />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

type UpdateCallback = ReturnType<typeof useUpdateCustomerProfileMutation>[1];

// ────────────────────────────────────────────────────────────────────────────────

const useFormSubmit = () => {
  const { t } = useLocalizationContext();
  const [submitResponse, updateMutation] = useUpdateCustomerProfileMutation();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  const handleFormSubmit = React.useCallback(
    async (input: Parameters<UpdateCallback>[0]) => {
      const response = await updateMutation(input);
      const systemError = getUrqlError(response.error);

      if (systemError) {
        const userError = t('account.profile.error');

        track('account.profile_save_failed', { userError, systemError });

        addNoticeBanner({
          text: userError,
          palette: 'caution',
        });
      }

      return response;
    },
    [updateMutation, t, track, addNoticeBanner],
  );

  return { submitResponse, handleFormSubmit };
};
